import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MessagingNotificationService, NewMessageAlert } from '../../services/messaging-notification.service';
import { SmsChatService } from '../../../modules/messaging/services/sms-chat.service';
import { CrmContactGroupsService } from '../../../modules/messaging/services/crm-contact-groups.service';

interface ToastEntry extends NewMessageAlert {
  id: number;
  leaving: boolean;
  /** SMS: the contact's name once resolved from their number. */
  contactName?: string;
}

const AUTO_DISMISS_MS = 6000;
const LEAVE_ANIMATION_MS = 250;

const CHANNEL_META: Record<NewMessageAlert['channel'], { icon: string; label: string; accent: string }> = {
  chat: { icon: 'bi-chat-dots-fill', label: 'New chat message', accent: '#3B82F6' },
  email: { icon: 'bi-envelope-fill', label: 'New email', accent: '#64748B' },
  sms: { icon: 'bi-chat-square-text-fill', label: 'New SMS', accent: '#0D9488' }
};

/**
 * Sound file: drop the .wav at src/assets/sounds/new-message.wav (folder
 * already wired into angular.json's "src/assets" glob, no config needed).
 */
const NOTIFICATION_SOUND_URL = 'assets/sounds/new-message.wav';

@Component({
  selector: 'app-message-toast',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './message-toast.component.html',
  styleUrl: './message-toast.component.css'
})
export class MessageToastComponent implements OnInit, OnDestroy {
  toasts: ToastEntry[] = [];
  channelMeta = CHANNEL_META;

  private nextId = 1;
  private subscription?: Subscription;
  private audio?: HTMLAudioElement;

  constructor(
    private messagingNotificationService: MessagingNotificationService,
    private router: Router,
    private smsChatService: SmsChatService,
    private crmContactGroupsService: CrmContactGroupsService
  ) {}

  ngOnInit(): void {
    try {
      this.audio = new Audio(NOTIFICATION_SOUND_URL);
    } catch {
      this.audio = undefined;
    }

    this.subscription = this.messagingNotificationService.newMessage$.subscribe((alert) => {
      // Already reading this conversation in the open SMS popup.
      if (alert.channel === 'sms' && this.smsChatService.sameNumber(alert.address, this.smsChatService.visibleNumber)) {
        return;
      }
      this.pushToast(alert);
      this.playSound();
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private pushToast(alert: NewMessageAlert): void {
    const id = this.nextId++;
    const entry: ToastEntry = { ...alert, id, leaving: false };
    if (alert.channel === 'sms' && alert.address) {
      entry.title = this.smsChatService.formatNumber(alert.address);
      this.resolveContactName(entry);
    }
    this.toasts = [entry, ...this.toasts].slice(0, 4);
    setTimeout(() => this.dismiss(id), AUTO_DISMISS_MS);
  }

  /** "New SMS · Sarah Mitchell" instead of a bare number, when the number
   *  belongs to a known contact. Best effort - keeps the number otherwise. */
  private resolveContactName(entry: ToastEntry): void {
    const address = entry.address as string;
    this.crmContactGroupsService.getPhoneNames([address]).subscribe({
      next: (names) => {
        const name = names[address] ?? Object.values(names)[0];
        if (name) {
          entry.contactName = name;
          entry.title = name;
        }
      },
      error: () => undefined
    });
  }

  private playSound(): void {
    if (!this.audio) {
      return;
    }
    // Autoplay can be blocked until the admin has interacted with the page
    // at least once - fail silently rather than throwing/console-spamming.
    this.audio.currentTime = 0;
    this.audio.play().catch(() => {});
  }

  dismiss(id: number): void {
    const toast = this.toasts.find((t) => t.id === id);
    if (!toast || toast.leaving) {
      return;
    }
    toast.leaving = true;
    setTimeout(() => {
      this.toasts = this.toasts.filter((t) => t.id !== id);
    }, LEAVE_ANIMATION_MS);
  }

  open(toast: ToastEntry): void {
    this.dismiss(toast.id);
    // SMS opens straight into the conversation; email/chat go to Messages.
    if (toast.channel === 'sms' && toast.address) {
      this.smsChatService.open({
        phoneNumber: toast.address,
        contactName: toast.contactName ?? null,
        contactSubtitle: null
      });
      return;
    }
    this.router.navigate(['/messages']);
  }

  openInbox(toast: ToastEntry, event: Event): void {
    event.stopPropagation();
    this.dismiss(toast.id);
    this.router.navigate(['/messages']);
  }
}
